import { computed } from "vue";
import { storeToRefs } from "pinia";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";
import { useUserStore } from "~/stores/user";
import { cleanParams } from "~/composables/useSiteComponentTracking";

const SESSION_KEY = "seer_user_center_session_id";
const PAGE_NAME = "user_center";

export const USER_CENTER_SUB_PAGE_MAP = {
	default: "profile_archive",
	file: "profile_archive",
	wallet: "wallet",
	order: "orders",
	follow: "following",
	message: "messages",
	coupon: "coupons",
	address: "address_management",
	account: "account_profile",
	logout: "logout",
	download: "app_download",
	language: "language",
};

export function resolveUserCenterSubPage(type) {
	const normalized = Array.isArray(type) ? type[0] : type;
	return USER_CENTER_SUB_PAGE_MAP[normalized || "default"] || String(normalized || "default");
}

function getSessionId() {
	if (!import.meta.client) return "";

	try {
		const cached = sessionStorage.getItem(SESSION_KEY);
		if (cached) return cached;

		const id =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		sessionStorage.setItem(SESSION_KEY, id);
		return id;
	} catch {
		return "";
	}
}

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";

	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

function getUserId(userInfo) {
	if (userInfo?.user_id || userInfo?.uid || userInfo?.id) {
		return userInfo.user_id || userInfo.uid || userInfo.id;
	}
	if (!import.meta.client) return "";

	try {
		const stored = JSON.parse(localStorage.getItem("gwUser") || "{}");
		return stored.user_id || stored.uid || stored.id || "";
	} catch {
		return "";
	}
}

export function useUserCenterTracking(deviceType) {
	const route = useRoute();
	const { locale } = useI18n();
	const { isLogin, userInfo } = storeToRefs(useUserStore());

	const currentSubPage = computed(() => resolveUserCenterSubPage(route.query.type));

	const baseParams = computed(() =>
		cleanParams({
			session_id: getSessionId(),
			user_id: getUserId(userInfo.value),
			login_status: isLogin.value ? "logged_in" : "guest",
			language: locale?.value === "en" ? "en" : "zh_hant",
			device_type: deviceType,
			page_name: PAGE_NAME,
			entry_source: getEntrySource(),
			sub_page: currentSubPage.value,
		})
	);

	function track(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	function trackPageView(params = {}) {
		track("user_center_page_view", params);
	}

	function trackNavClick(navItem, params = {}) {
		track("user_center_nav_click", {
			nav_item: resolveUserCenterSubPage(navItem),
			previous_sub_page: currentSubPage.value,
			...params,
		});
	}

	function trackCustomerSupportClick(params = {}) {
		track("customer_support_click", params);
	}

	return {
		track,
		trackPageView,
		trackNavClick,
		trackCustomerSupportClick,
		currentSubPage,
		baseParams,
	};
}
